import { useCallback, useMemo } from "react";

import { SelectionContextValue } from "./contexts";
import { SelectionProp } from "./tableProps";
import { TableKey, TableRow } from "./types";

export interface RowCheckboxState {
    checked: boolean;
    indeterminate: boolean;
    onToggle: () => void;
}

export interface HeaderCheckboxState {
    checked: boolean;
    indeterminate: boolean;
    onToggle: () => void;
}

interface UseSelectionParams<TRow, TDetail> {
    isGrouped: boolean;
    selection: SelectionProp<TRow> | undefined;
    visibleRows: TableRow<TRow>[] | undefined;
    /** Items that are selectable for a given top-level row. Flat: `[row.data]`. Grouped: `grouping.getItems(row.key)`. */
    getItemsForRow: (row: TableRow<TRow>) => (TRow | TDetail)[];
    /** Selection key for an item. Flat: `getRowKey`. Grouped: `getDetailKey`. */
    getKey: (item: TRow | TDetail) => TableKey;
    /** Used when `expandOnGroupSelect` is enabled (grouped mode only). */
    setGroupsExpanded?: (keys: TableKey[], expanded: boolean) => void;
}

interface UseSelectionResult {
    headerCheckbox: HeaderCheckboxState | undefined;
    rowCheckboxByKey: Map<TableKey, RowCheckboxState>;
    selectionContextValue: SelectionContextValue | null;
}

/**
 * Derives header / per-row / per-child checkbox state from `selection`. Pre-computes
 * all per-row states into a `Map` once per render, replacing the previous design's
 * inline `buildGroupCheckbox` factory that ran per row per render.
 *
 * Returns `null` for `selectionContextValue` when selection is disabled — `GroupedBody`
 * skips mounting the context provider in that case.
 */
export function useSelection<TRow, TDetail>(params: UseSelectionParams<TRow, TDetail>): UseSelectionResult {
    const { isGrouped, selection, visibleRows, getItemsForRow, getKey, setGroupsExpanded } = params;

    const rowCheckboxByKey = useMemo<Map<TableKey, RowCheckboxState>>(() => {
        const map = new Map<TableKey, RowCheckboxState>();
        if (!selection || !visibleRows) return map;

        for (const row of visibleRows) {
            const items = getItemsForRow(row);
            const keys = items.map(getKey);
            let selectedCount = 0;
            for (const k of keys) if (selection.selected.has(k)) selectedCount += 1;
            const checked = keys.length > 0 && selectedCount === keys.length;
            const indeterminate = selectedCount > 0 && selectedCount < keys.length;

            const onToggle = () => {
                if (keys.length === 0) return;
                const next = new Set(selection.selected);
                if (selectedCount > 0) {
                    for (const k of keys) next.delete(k);
                } else {
                    for (const k of keys) next.add(k);
                    if (isGrouped && selection.expandOnGroupSelect) {
                        setGroupsExpanded?.([row.key], true);
                    }
                }
                selection.setSelected(next);
            };

            map.set(row.key, { checked, indeterminate, onToggle });
        }
        return map;
    }, [selection, visibleRows, getItemsForRow, getKey, isGrouped, setGroupsExpanded]);

    const headerCheckbox = useMemo<HeaderCheckboxState | undefined>(() => {
        if (!selection || !visibleRows) return undefined;

        const allItems: (TRow | TDetail)[] = [];
        for (const row of visibleRows) allItems.push(...getItemsForRow(row));
        const keys = allItems.map(getKey);
        let selectedCount = 0;
        for (const k of keys) if (selection.selected.has(k)) selectedCount += 1;
        const checked = keys.length > 0 && selectedCount === keys.length;
        const indeterminate = selectedCount > 0 && selectedCount < keys.length;

        const onToggle = () => {
            if (keys.length === 0) return;
            const next = new Set(selection.selected);
            if (selectedCount > 0) {
                for (const k of keys) next.delete(k);
            } else {
                for (const k of keys) next.add(k);
                if (isGrouped && selection.expandOnGroupSelect) {
                    setGroupsExpanded?.(
                        visibleRows.map((r) => r.key),
                        true
                    );
                }
            }
            selection.setSelected(next);
        };

        return { checked, indeterminate, onToggle };
    }, [selection, visibleRows, getItemsForRow, getKey, isGrouped, setGroupsExpanded]);

    // Context-mounted helpers for individual child-row checkboxes. New value object
    // on every selection change (correct: rows that subscribe need to re-render).
    const isSelected = useCallback(
        (item: unknown) => !!selection && selection.selected.has(getKey(item as TDetail)),
        [selection, getKey]
    );
    const toggle = useCallback(
        (item: unknown) => {
            if (!selection) return;
            const key = getKey(item as TDetail);
            const next = new Set(selection.selected);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            selection.setSelected(next);
        },
        [selection, getKey]
    );

    const selectionContextValue = useMemo<SelectionContextValue | null>(
        () =>
            selection
                ? {
                      isSelected,
                      toggle,
                      getDetailKey: (item: unknown) => getKey(item as TDetail)
                  }
                : null,
        [selection, isSelected, toggle, getKey]
    );

    return { headerCheckbox, rowCheckboxByKey, selectionContextValue };
}
